import React, { useState } from 'react'
import useTrans from '@hooks/useTrans'
import useFindMenu from '@hooks/useFindMenu'
import { getIcon } from '@utils/icons'
import { ChevronDown } from '@components/icons'
import { Logo, WContainer, WLink } from '@components/atoms'
import styles from './Footer.module.sass'
import { useRouter } from 'next/router'
import { handleFooterTracking, handleSocialTracking } from '@utils/tracking'
import { BRAND } from '@utils/settings'
import useAriaLabel from 'hooks/useAriaLabel'

interface Props {
  className?: string
}

interface FooterMenuLinkProps {
  item: { name?: string; url?: string; key?: string; blank?: boolean }
  sectionKey?: string
  className: string
}

const FooterMenuLink = ({ item, sectionKey, className }: FooterMenuLinkProps) => {
  const computedAriaLabel = useAriaLabel(undefined, item.name, item.url)

  return (
    <WLink
      href={item.url}
      onClick={() => handleFooterTracking(item.key, sectionKey)}
      newPage={item.blank}
      className={className}
      ariaLabel={computedAriaLabel}
    >
      {item.name}
    </WLink>
  )
}

const Footer = (props: Props) => {
  const { className = '' } = props

  const t = useTrans()
  const router = useRouter()

  const footerMenu = useFindMenu('footer')
  const socialMenu = useFindMenu('social')

  return (
    <footer className={`${styles.root} ${className}`}>
      <div className={styles.main}>
        <WContainer>
          <div className={styles.mainLayout}>
            <div className={styles.left}>
              <WLink href={'/'} ariaLabel={BRAND} className={styles.logo}>
                <Logo />
              </WLink>
              <div className={styles.divider} />

              {footerMenu?.children.map((x, index) => (
                <div key={x.pk} className={`${styles.col} ${styles.footerMenusDesk}`}>
                  <div className={`${styles.menu}`}>
                    <div className={styles.menuTitle}>
                      <span
                        className={`${styles.menuTitleLabel} ${styles['menuTitleLabel--mobile']}`}
                      >
                        {x.name}
                      </span>
                    </div>
                    <div
                      id={`footermenu_${x.pk}_panel`}
                      aria-labelledby={`footermenu_${x.pk}_trigger`}
                      className={styles.menuPanel}
                    >
                      <ul className={styles.menuList}>
                        {x.children?.map((y) => (
                          <li key={y.pk}>
                            <FooterMenuLink item={y} sectionKey={x.key} className={styles.menuItem} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!!socialMenu?.children?.length && (
              <ul className={styles.socialMenu}>
                {socialMenu.children.map((x) => (
                  <li key={x.pk}>
                    <WLink
                      href={x.url}
                      className={styles.socialItem}
                      onClick={() => handleSocialTracking(x.name)}
                      newPage
                      external
                    >
                      {!!getIcon(x.icon) && (
                        <span className={styles.socialItemIcon}>{getIcon(x.icon)}</span>
                      )}
                      {!!x.name && <span className={styles.socialItemLabel}>{x.name}</span>}
                    </WLink>
                  </li>
                ))}
              </ul>
            )}

            {footerMenu?.children.map((x) => (
              <div key={x.pk} className={`${styles.col} ${styles.footerMenuMobile}`}>
                <div className={`${styles.menu}`}>
                  {x.children?.length > 0 &&
                    x.children.map((y) => (
                      <FooterMenuLink
                        key={y.pk}
                        item={y}
                        sectionKey={x.key}
                        className={styles.menuItem}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </WContainer>
      </div>
      <div className={styles.bottom}>
        <WContainer>
          <div
            dangerouslySetInnerHTML={{
              __html: t(`footer_${process.env.SITE_ID}_${router.locale}`),
            }}
          />
        </WContainer>
      </div>
    </footer>
  )
}

export default Footer
